// Pure reward calculation helpers — no Firebase, no React.
// All values come from rewardConfig so behaviour is controlled from one place.

import {
  LEVEL_THRESHOLDS,
  MAX_LEVEL,
  MAX_COINS_PER_GAME,
  MIN_COINS_FOR_ATTEMPT,
  EXP_BASE_PER_MINIGAME,
  EXP_MAX_BONUS_PER_MINIGAME
} from '@/constants/rewardConfig'

// How many coins to award for a scored minigame.
// scorePercent must be 0–100.
// Returns a whole number between MIN_COINS_FOR_ATTEMPT and MAX_COINS_PER_GAME.
export function calculateMinigameCoins(scorePercent) {
  if (typeof scorePercent !== 'number' || scorePercent < 0) {
    return MIN_COINS_FOR_ATTEMPT
  }
  const clamped = Math.min(scorePercent, 100)
  return Math.max(
    MIN_COINS_FOR_ATTEMPT,
    Math.round((clamped / 100) * MAX_COINS_PER_GAME)
  )
}

// How much EXP to award for a scored minigame.
// Base EXP is always given. Bonus EXP scales with score.
// scorePercent must be 0–100.
export function calculateMinigameExp(scorePercent) {
  if (typeof scorePercent !== 'number' || scorePercent < 0) {
    return EXP_BASE_PER_MINIGAME
  }
  const clamped = Math.min(scorePercent, 100)
  const bonus = Math.round((clamped / 100) * EXP_MAX_BONUS_PER_MINIGAME)
  return EXP_BASE_PER_MINIGAME + bonus
}

// Convert a raw score (e.g. 8 correct out of 10) to a percentage.
// Returns 0 if maxScore is 0 or invalid.
export function toScorePercent(score, maxScore) {
  if (!maxScore || maxScore <= 0) return 0
  return Math.min(100, Math.max(0, (score / maxScore) * 100))
}

// Given a cumulative EXP total, return the matching level object.
// Always returns a valid level (falls back to level 12 at max).
export function getLevelFromExp(totalExp) {
  if (typeof totalExp !== 'number' || totalExp < 0) {
    return LEVEL_THRESHOLDS[0]
  }
  // Walk from highest level down to find the first threshold the user has passed
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (totalExp >= LEVEL_THRESHOLDS[i].minExp) {
      return LEVEL_THRESHOLDS[i]
    }
  }
  return LEVEL_THRESHOLDS[0]
}

// How many EXP points until the next level.
// Returns 0 if already at max level.
export function getExpToNextLevel(totalExp) {
  const current = getLevelFromExp(totalExp)
  if (current.level >= MAX_LEVEL) return 0
  const nextThreshold = LEVEL_THRESHOLDS.find(
    (t) => t.level === current.level + 1
  )
  if (!nextThreshold) return 0
  return nextThreshold.minExp - totalExp
}

// Progress percentage (0–100) within the current level band — used for a progress bar.
export function getLevelProgress(totalExp) {
  const current = getLevelFromExp(totalExp)
  if (current.level >= MAX_LEVEL) return 100

  const nextThreshold = LEVEL_THRESHOLDS.find(
    (t) => t.level === current.level + 1
  )
  if (!nextThreshold) return 100

  const bandSize = nextThreshold.minExp - current.minExp
  const progress = totalExp - current.minExp
  return Math.min(100, Math.max(0, Math.round((progress / bandSize) * 100)))
}
